import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { PublicPageTemplate } from "@/components/public";
import { getCanonicalUrl } from "@/config/site";
import {
  createBreadcrumbSchema,
  createCollectionPageSchema,
  createOrganizationSchema,
} from "@/config/seo";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { SegmentLandingPageProps, SegmentPillar } from "@/types/marketing/segment-page";

/* ─── Ken Burns variants ─── */
const kenBurnsVariants = [
  { scale: [1, 1.04], x: [0, -8], y: [0, -5] },
  { scale: [1, 1.05], x: [0, 8], y: [0, -4] },
  { scale: [1, 1.03], x: [0, -5], y: [0, 6] },
  { scale: [1, 1.04], x: [0, 6], y: [0, 4] },
];

/* ─── Segment-specific icon mapping ─── */
const segmentIcons: Record<string, string> = {
  business: "/icons/who-we-serve/icon-who-business.png",
  developers: "/icons/who-we-serve/icon-who-developers.png",
  partners: "/icons/who-we-serve/icon-who-partners.png",
  consumers: "/icons/who-we-serve/icon-who-consumers.png",
};

/* ─── Render CTA action (internal Link or external <a>) ─── */
const renderAction = (href: string, children: React.ReactNode) => {
  const isExternal = href.startsWith("http://") || href.startsWith("https://");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }
  return <Link to={href}>{children}</Link>;
};

/* ═══════════════════════════════════════════════════════
   Typewriter Pillar (from SolutionFinalCTA pattern)
   ═══════════════════════════════════════════════════════ */
const TypewriterPillar = ({
  pillar,
  isActive,
  isComplete,
  onComplete,
}: {
  pillar: SegmentPillar;
  isActive: boolean;
  isComplete: boolean;
  onComplete: () => void;
}) => {
  const [displayText, setDisplayText] = useState("");
  const [showDescription, setShowDescription] = useState(false);

  useEffect(() => {
    if (!isActive || isComplete) return;
    const target = pillar.title;
    let i = 0;
    const interval = setInterval(() => {
      if (i < target.length) {
        setDisplayText(target.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
        setShowDescription(true);
        setTimeout(onComplete, 300);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [isActive, isComplete, pillar.title, onComplete]);

  useEffect(() => {
    if (isComplete) {
      setDisplayText(pillar.title);
      setShowDescription(true);
    }
  }, [isComplete, pillar.title]);

  return (
    <div className="text-center p-4 sm:p-6 group hover:scale-105 transition-transform duration-300">
      <h3
        className={`text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${pillar.gradient} mb-3 sm:mb-4 drop-shadow-lg whitespace-pre-line min-h-[2.5em] sm:min-h-[2.8em] md:min-h-[3em] flex items-center justify-center`}
      >
        {displayText}
        {isActive && !isComplete && (
          <span className="animate-pulse text-white/80">|</span>
        )}
      </h3>
      <p
        className={`text-gray-100 text-sm sm:text-base md:text-lg drop-shadow-md transition-opacity duration-500 ${showDescription ? "opacity-100" : "opacity-0"}`}
      >
        {pillar.description}
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   Feature Card with scroll entrance animation
   ═══════════════════════════════════════════════════════ */
const CapabilityCard = ({
  capability,
  index,
}: {
  capability: { title: string; description: string; image: string };
  index: number;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isImageLeft = index % 2 === 0;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -30px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isImageLeft ? "lg:flex-row" : "lg:flex-row-reverse"} items-center gap-6 sm:gap-8 lg:gap-12 xl:gap-16`}
    >
      {/* Image */}
      <div
        className={`w-full lg:w-1/2 transition-all duration-700 ease-out ${
          isVisible
            ? "opacity-100 translate-y-0 lg:translate-x-0"
            : `opacity-0 translate-y-8 lg:translate-y-0 ${isImageLeft ? "lg:-translate-x-16" : "lg:translate-x-16"}`
        }`}
      >
        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-lg group">
          <img
            src={capability.image}
            alt={capability.title}
            loading="lazy"
            className="w-full h-48 sm:h-64 md:h-80 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-pink-500/15 to-blue-600/30 rounded-xl sm:rounded-2xl" />
        </div>
      </div>

      {/* Text */}
      <div
        className={`w-full lg:w-1/2 ${isImageLeft ? "lg:pl-4" : "lg:pr-4"} transition-all duration-700 ease-out delay-150 text-center lg:text-left ${
          isVisible
            ? "opacity-100 translate-y-0 lg:translate-x-0"
            : `opacity-0 translate-y-8 lg:translate-y-0 ${isImageLeft ? "lg:translate-x-16" : "lg:-translate-x-16"}`
        }`}
      >
        <div className="h-1 w-12 sm:w-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full mb-3 sm:mb-4 mx-auto lg:mx-0" />
        <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
          {capability.title}
        </h3>
        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-md mx-auto lg:mx-0 lg:max-w-none">
          {capability.description}
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════ */
const SegmentLandingPage = ({ content }: SegmentLandingPageProps) => {
  const isConsumersPage = content.slug === "consumers";
  const iconSrc = segmentIcons[content.slug] || segmentIcons.business;

  /* ── Hero rotating word ── */
  const [wordIndex, setWordIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setWordIndex((p) => (p + 1) % content.heroRotatingWords.length),
      3500
    );
    return () => clearInterval(timer);
  }, [content.heroRotatingWords.length]);

  useEffect(() => {
    const timer = setInterval(
      () => setImageIndex((p) => (p + 1) % kenBurnsVariants.length),
      8000
    );
    return () => clearInterval(timer);
  }, []);

  /* ── CTA Pillar typewriter state ── */
  const pillarRef = useRef<HTMLDivElement>(null);
  const [activePillarIndex, setActivePillarIndex] = useState(-1);
  const [completedPillars, setCompletedPillars] = useState<number[]>([]);
  const [pillarStarted, setPillarStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !pillarStarted) {
          setPillarStarted(true);
          setActivePillarIndex(0);
        }
      },
      { threshold: 0.3 }
    );
    if (pillarRef.current) observer.observe(pillarRef.current);
    return () => observer.disconnect();
  }, [pillarStarted]);

  const handlePillarComplete = useCallback(
    (index: number) => {
      setCompletedPillars((prev) => [...prev, index]);
      if (index < content.pillars.length - 1) setActivePillarIndex(index + 1);
    },
    [content.pillars.length]
  );

  const currentWord = content.heroRotatingWords[wordIndex];

  /* ── SEO ── */
  const seoConfig = {
    title: `${content.title} | Help Genie`,
    description: content.seoDescription,
    ogTitle: content.title,
    ogDescription: content.seoDescription,
    canonicalUrl: getCanonicalUrl(`/help-genie-for-${content.slug}`),
    structuredData: [
      createBreadcrumbSchema([
        { name: "Home", url: getCanonicalUrl("/") },
        {
          name: content.title,
          url: getCanonicalUrl(`/help-genie-for-${content.slug}`),
        },
      ]),
      createCollectionPageSchema(
        content.title,
        content.seoDescription,
        getCanonicalUrl(`/help-genie-for-${content.slug}`)
      ),
      createOrganizationSchema(),
    ],
  };

  return (
    <PublicPageTemplate seo={seoConfig}>
      {/* ════════════════════════════════════════════
          HERO — Ken Burns + rotating word + shimmer
         ════════════════════════════════════════════ */}
      <section className="relative w-full h-[65vh] sm:h-[78vh] md:h-[84vh] min-h-[480px] sm:min-h-[600px] md:min-h-[660px] flex items-center text-white overflow-hidden">
        {/* Background with Ken Burns */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence mode="sync">
            <motion.img
              key={imageIndex}
              src={content.imagery.heroImage}
              alt={`${content.title} hero background`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1 }}
              animate={{
                opacity: 1,
                scale: kenBurnsVariants[imageIndex].scale,
                x: kenBurnsVariants[imageIndex].x,
                y: kenBurnsVariants[imageIndex].y,
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.2, ease: "easeInOut" },
                scale: { duration: 8, ease: "linear" },
                x: { duration: 8, ease: "linear" },
                y: { duration: 8, ease: "linear" },
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/hero-background.webp";
              }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/70 z-10" />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl">
            {/* Segment icon */}
            <div className="mb-4">
              <img
                src={iconSrc}
                alt={content.audienceLabel}
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain mx-auto drop-shadow-2xl"
              />
            </div>

            {/* Audience tag */}
            <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full">
              <span className="text-sm sm:text-base font-semibold text-white">
                {content.audienceLabel}
              </span>
              {isConsumersPage && (
                <span className="ml-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide">
                  App Coming Soon
                </span>
              )}
            </div>

            {/* Headline with rotating word */}
            <h1
              className="font-bold leading-none drop-shadow-2xl mb-6"
              style={{ fontSize: "clamp(2.5rem, 6vw, 7rem)" }}
            >
              <div className="text-white">{content.heroHeadline}</div>
              <div className="whitespace-nowrap">
                <span
                  key={currentWord}
                  className="animate-shimmer bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(110deg, #60a5fa 0%, #a855f7 25%, #ec4899 50%, #a855f7 75%, #60a5fa 100%)",
                    backgroundSize: "200% 100%",
                  }}
                >
                  {currentWord}
                </span>
                {(content.heroSuffix === undefined ? "again" : content.heroSuffix) && (
                  <>{" "}<span className="text-white">{content.heroSuffix === undefined ? "again" : content.heroSuffix}</span></>
                )}
              </div>
            </h1>

            <p
              className="mt-6 text-white font-normal drop-shadow-lg max-w-5xl mx-auto"
              style={{ fontSize: "clamp(1.125rem, 1.75vw, 1.5rem)" }}
            >
              {content.heroSubheadline}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-lg hover:from-purple-700 hover:to-blue-700 transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8"
                style={{
                  fontSize: "clamp(1rem, 1.2vw, 1.25rem)",
                  padding:
                    "clamp(0.875rem, 1vw, 1rem) clamp(1.5rem, 2vw, 2rem)",
                }}
              >
                {renderAction(
                  content.primaryCTA.href,
                  <>
                    {content.primaryCTA.label}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-lg bg-white/10 text-white border-2 border-white/50 hover:bg-white/20 hover:border-white font-semibold shadow-lg backdrop-blur-sm transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8"
                style={{
                  fontSize: "clamp(1rem, 1.2vw, 1.25rem)",
                  padding:
                    "clamp(0.875rem, 1vw, 1rem) clamp(1.5rem, 2vw, 2rem)",
                }}
              >
                {renderAction(
                  content.secondaryCTA.href,
                  <>
                    <Phone className="mr-2 h-5 w-5" />
                    {content.secondaryCTA.label}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Shimmer keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .animate-shimmer { animation: shimmer 3s ease-in-out infinite; }
      `}</style>

      {/* ════════════════════════════════════════════
          CAPABILITIES — Alternating image/text cards
         ════════════════════════════════════════════ */}
      <section className="w-full bg-white py-12 sm:py-16 md:py-24 lg:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold px-2 pb-2 leading-tight text-gray-900">
              {content.capabilityTitle}
            </h2>
            <p className="mt-4 text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {content.capabilitySubtitle}
            </p>
          </div>

          <div className="space-y-10 sm:space-y-12 md:space-y-16 lg:space-y-20">
            {content.capabilities.map((cap, index) => (
              <CapabilityCard
                key={cap.title}
                capability={cap}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          USE CASES — Rich 2x2 grid with images
         ════════════════════════════════════════════ */}
      <section className="w-full bg-[#F9FAFC] py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              {content.useCaseTitle}
            </h2>
            <p className="mt-4 text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {content.useCaseSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {content.useCases.map((uc) => (
              <div
                key={uc.title}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group"
              >
                {/* Image */}
                <div className="h-48 sm:h-56 overflow-hidden relative">
                  <img
                    src={uc.image}
                    alt={uc.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-500/10 to-blue-600/20" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                    {uc.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                    {uc.description}
                  </p>
                  <ul className="space-y-2">
                    {uc.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex items-start gap-2 text-sm text-gray-700"
                      >
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          PROCESS — Visual step cards with connectors
         ════════════════════════════════════════════ */}
      <section className="w-full bg-white py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              {content.processTitle}
            </h2>
            <p className="mt-4 text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {content.processSubtitle}
            </p>
          </div>

          <div className="relative">
            {/* Desktop connector line */}
            <div className="hidden lg:block absolute top-16 left-[16.67%] right-[16.67%] h-0.5 bg-gradient-to-r from-purple-300 via-blue-300 to-purple-300" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
              {content.processSteps.map((step, index) => (
                <div key={step.title} className="relative text-center">
                  {/* Step number */}
                  <div className="relative mx-auto mb-6 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-blue-600 text-xl sm:text-2xl font-bold text-white shadow-lg z-10">
                    {index + 1}
                  </div>

                  {/* Mobile connector */}
                  {index < content.processSteps.length - 1 && (
                    <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-16 w-0.5 h-6 bg-gradient-to-b from-purple-300 to-blue-300" />
                  )}

                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed max-w-xs mx-auto">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          FINAL CTA — Background image + typewriter pillars
         ════════════════════════════════════════════ */}
      <section className="relative w-full py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={content.imagery.ctaBackgroundImage || content.imagery.heroImage}
            alt="Background"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/75 to-black/80" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4 sm:mb-6 drop-shadow-2xl">
              {content.finalCTAHeadline}
            </h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed sm:leading-8 text-gray-100 max-w-4xl mx-auto drop-shadow-lg px-2 sm:px-0">
              {content.finalCTACopy}
            </p>
          </div>

          {/* Typewriter pillars */}
          <div ref={pillarRef} className="mb-12 sm:mb-16 md:mb-20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 max-w-6xl mx-auto">
              {content.pillars.map((pillar, index) => (
                <TypewriterPillar
                  key={pillar.title}
                  pillar={pillar}
                  isActive={activePillarIndex === index}
                  isComplete={completedPillars.includes(index)}
                  onComplete={() => handlePillarComplete(index)}
                />
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="text-center mt-4 sm:mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-6 px-4 sm:px-0">
              <a
                href={content.finalSecondaryCTA.href}
                className="px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 bg-transparent hover:bg-white/10 active:bg-white/20 text-white hover:text-gray-100 font-semibold rounded-xl border-2 border-white/50 hover:border-white/70 transition-all duration-300 text-sm sm:text-base md:text-lg w-full sm:w-auto text-center backdrop-blur-sm max-w-[280px] sm:max-w-none min-h-[48px] touch-manipulation"
              >
                {content.finalSecondaryCTA.label}
              </a>
              <a
                href={content.finalPrimaryCTA.href}
                className="px-6 sm:px-8 md:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 active:from-purple-800 active:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-sm sm:text-base md:text-lg w-full sm:w-auto text-center max-w-[280px] sm:max-w-none min-h-[48px] touch-manipulation"
              >
                {content.finalPrimaryCTA.label}
              </a>
            </div>
          </div>
        </div>
      </section>
    </PublicPageTemplate>
  );
};

export default SegmentLandingPage;
